import {
    Button,
    Card,
    ChevronRightIcon,
    CircleAlertIcon,
    CircleCheckIcon,
    CircleIcon,
    ClockIcon,
    DirhamIcon,
    ReceiptTextIcon,
    Skeleton,
    Typography
} from '@/components/atoms';
import { PaymentCardSkeleton, ProfileNotificationHeader } from '@/components/molecules';
import PaymentDetailsDialog from '@/components/organisms/PaymentDetailsDialog';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { useTranslation, useUIColors } from '@/core/hooks';
import { logger } from '@/core/logger';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { fetchHomeCoverImage } from '@/core/services/contentfulClient';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { trackDynatraceEvent } from '@/core/services/dynatraceService';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { useAppStore } from '@/core/stores/appStore';
import { getUserFirstName } from '@/core/utils';
import { getGreetingTranslationKey } from '@/core/utils/greeting';
import { useProfile } from '@/modules/auth/hooks/useProfile';
import { useUnitInstallments } from '@/modules/auth/hooks/useUnitInstallments';
import { useVentures } from '@/modules/home/hooks/useVentures';
import { usePasscode } from '@/modules/passcode';
import { shouldInvokeSkipPasscodeApi } from '@/modules/passcode/utils/skipPasscodeGate';
import {
    getAllOverdueInstallments,
    getAllPaidInstallments,
    getAllUpcomingDueInstallments,
    mapInstallmentStatusToPaymentStatus,
    selectOnlinePaymentIds,
} from '@/modules/payments/utils/installmentUtils';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { router, useLocalSearchParams } from 'expo-router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, ScrollView, StyleSheet, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

interface PaymentCardData {
    id: string;
    propertyName: string;
    amount: number;
    paymentCount: number;
}

interface PaymentUpdateData {
    id: string;
    propertyName: string;
    paymentTitle: string;
    amount: number;
    status: 'in-process' | 'confirmed';
    paymentDate: string;
}

export default function HomeScreen() {
    const venturesQuery = useVentures();
    const colors = useUIColors();
    const { t } = useTranslation();
    const { isRTL } = useI18nContext();
    const insets = useSafeAreaInsets();
    const params = useLocalSearchParams<{ skipPasscode?: string }>();
    const skipPasscodeCalledRef = useRef(false);
    const { width: screenWidth } = useWindowDimensions();
    const [paymentDetailsVisible, setPaymentDetailsVisible] = useState(false);
    const [selectedPaymentId, setSelectedPaymentId] = useState<string | undefined>(undefined);

    // Profile fetching hook
    const { isLoading: profileLoading, error: profileError, fetchProfile, retry } = useProfile();

    // Unit installments fetching hook
    const { fetchAllUnitInstallments } = useUnitInstallments();

    // Passcode hook for skip passcode
    const { skipPasscode } = usePasscode();

    // Get authentication state to prevent API calls when logged out
    const isAuthenticated = useAppStore((state) => state.auth.isAuthenticated);

    // Get loading states from store
    const unitInstallmentsLoading = useAppStore((state) => state.unitInstallmentsLoading);

    // State for home cover image
    const [homeCoverImage, setHomeCoverImage] = React.useState<string | null>(null);

    useEffect(() => {
        trackDynatraceEvent({
            p_screen: 'home',
            p_actionName: 'Homepage Viewed',
            eventType: 'view',
        });
    }, []);

    const navigateToPropertiesList = useCallback(() => {
        trackCatalogEvent({
            screen: DT_SCREEN.HOME,
            actionName: 'home_view_all_properties_click',
            eventType: 'action',
        });
        router.push('/(auth)/(home)/properties' as never);
    }, []);

    const trackPayAllMissed = useCallback(() => {
        trackCatalogEvent({
            screen: DT_SCREEN.HOME,
            actionName: 'home_pay_all_click',
            eventType: 'action',
        });
    }, []);

    // Auto skip passcode on web after email/password login only
    // Only call if we don't already have a passcodeToken (skip was already called)
    const webSkipPasscodeCalledRef = useRef(false);
    useEffect(() => {
        if (Platform.OS === 'web' && isAuthenticated && !webSkipPasscodeCalledRef.current) {
            // UAE Pass / Emirates ID / ADHA — never auto-skip passcode
            if (!shouldInvokeSkipPasscodeApi()) {
                const { auth, isAdhaJourney } = useAppStore.getState();
                webSkipPasscodeCalledRef.current = true;
                logger.info('HomeScreen:Auto skip passcode skipped — not email/password login', {
                    loginMethod: auth.loginMethod,
                    isAdhaJourney,
                });
                return;
            }

            // Wait for token to be available before calling skip passcode
            const checkTokenAndSkip = async () => {
                const authState = useAppStore.getState().auth;

                // If we already have a passcodeToken, skip passcode was already called, don't call again
                if (authState.passcodeToken) {
                    logger.info('HomeScreen:PasscodeToken already exists, skipping auto-skip passcode call');
                    webSkipPasscodeCalledRef.current = true;
                    return;
                }

                if (!authState.token) {
                    // Token not available yet, wait a bit and retry
                    logger.debug('HomeScreen:Token not available yet, waiting...');
                    setTimeout(() => {
                        void checkTokenAndSkip();
                    }, 100);
                    return;
                }

                webSkipPasscodeCalledRef.current = true;
                logger.info('HomeScreen:Web platform detected, auto-skipping passcode (email/password)');

                try {
                    await skipPasscode('login');
                    logger.info('HomeScreen:Web auto skip passcode successful');
                } catch (err) {
                    logger.error('HomeScreen:Web auto skip passcode failed', { error: err });
                    // Continue with normal flow even if skip fails
                }
            };

            void checkTokenAndSkip();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    // Fetch home cover image from Contentful
    // Use 'home-cover-web' for web platform, 'home-cover' for mobile
    React.useEffect(() => {
        const imageKey = Platform.OS === 'web' ? 'home-cover-web' : 'home-cover';
        fetchHomeCoverImage(imageKey)
            .then((url) => {
                if (url) {
                    setHomeCoverImage(url);
                }
            })
            .catch((err) => {
                logger.error('HomeScreen', 'Failed to fetch home cover image', { error: err, imageKey });
            });
    }, []);

    // Handle skip passcode on login (email/password only; query set by useLogin)
    useEffect(() => {
        if (params.skipPasscode === 'true' && !skipPasscodeCalledRef.current) {
            if (!shouldInvokeSkipPasscodeApi()) {
                const { auth, isAdhaJourney } = useAppStore.getState();
                skipPasscodeCalledRef.current = true;
                logger.info('HomeScreen:Skip passcode flag ignored — not email/password login', {
                    loginMethod: auth.loginMethod,
                    isAdhaJourney,
                });
                return;
            }

            skipPasscodeCalledRef.current = true;
            logger.info('HomeScreen:Skip passcode flag detected, calling skip passcode API');

            const loadProfileAndInstallments = async (
                failureMessage: string,
                logProfileFetched: boolean,
            ) => {
                if (!isAuthenticated) {
                    return;
                }
                try {
                    await fetchProfile();
                    if (logProfileFetched) {
                        logger.debug('HomeScreen:Profile fetched after skip passcode, fetching unit installments');
                    }
                    await fetchAllUnitInstallments();
                } catch (err) {
                    logger.error(failureMessage, { error: err });
                }
            };

            void (async () => {
                try {
                    await skipPasscode('login');
                    logger.info('HomeScreen:Skip passcode successful, proceeding with normal flow');
                    await loadProfileAndInstallments(
                        'HomeScreen:Failed to fetch profile or installments after skip passcode',
                        true,
                    );
                } catch (err) {
                    logger.error('HomeScreen:Failed to skip passcode', { error: err });
                    await loadProfileAndInstallments(
                        'HomeScreen:Failed to fetch profile or installments',
                        false,
                    );
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.skipPasscode, isAuthenticated]);

    // Fetch profile on mount - only when authenticated
    // CRITICAL: fetchProfile and fetchAllUnitInstallments are stable (don't change on re-renders)
    // Only depend on isAuthenticated to prevent infinite loops
    // Skip if skipPasscode is being handled (it will fetch after skip completes)
    useEffect(() => {
        if (!isAuthenticated) {
            logger.debug('HomeScreen:Skip profile fetch - user not authenticated');
            return;
        }

        // Don't fetch if skip passcode is being handled (it will fetch after skip completes)
        if (params.skipPasscode === 'true' && !skipPasscodeCalledRef.current) {
            logger.debug('HomeScreen:Skip profile fetch - waiting for skip passcode');
            return;
        }

        let isMounted = true;

        fetchProfile()
            .then(() => {
                if (!isMounted) return;
                // After profile is fetched successfully, fetch unit installments
                // This ensures we have properties before fetching installments
                logger.debug('HomeScreen:Profile fetched, fetching unit installments');
                return fetchAllUnitInstallments();
            })
            .catch((err) => {
                if (!isMounted) return;
                // Error handled by hook state
                logger.error('HomeScreen:Failed to fetch profile or installments', { error: err });
            });

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, params.skipPasscode]); // Only depend on isAuthenticated and skipPasscode - functions are stable

    const handlePaymentCardPress = (propertyId: string, category: 'overdue' | 'upcomingDue') => {
        // Get pre-computed payment IDs for instant lookup (no processing needed)
        const paymentIds = category === 'overdue'
            ? overduePaymentIdsMap.get(propertyId) || []
            : upcomingDuePaymentIdsMap.get(propertyId) || [];
        const canPayOnline = properties.find((property) => property.id === propertyId)?.isOnlinePaymentEnabled === true;

        // Pre-select only when this unit allows online payment (same profile row as the unit name).
        if (canPayOnline && paymentIds.length > 0) {
            router.push({
                pathname: '/(auth)/property-payments',
                params: {
                    tab: 'upcoming',
                    selectedPaymentIds: paymentIds.join(','),
                },
            } as any);
        } else {
            router.push({
                pathname: '/(auth)/property-payments',
                params: {
                    tab: 'upcoming',
                },
            } as any);
        }
    };

    const handleVenturePress = (id: string) => {
        router.push({ pathname: '/venture/[id]', params: { id } } as any);
    };

    // Get user name for greeting
    const user = useAppStore((state) => state.auth.user);

    // Extract first name using reusable utility function
    const userName = getUserFirstName(user);

    // Get real data from store
    const properties = useAppStore((state) => state.properties);
    const unitInstallments = useAppStore((state) => state.unitInstallments);

    // Pre-compute payment IDs map for fast lookup (overdue)
    const overduePaymentIdsMap = useMemo(() => {
        const overdueItems = getAllOverdueInstallments(unitInstallments, properties);
        const map = new Map<string, string[]>();

        overdueItems.forEach((item) => {
            const existing = map.get(item.propertyId) || [];
            existing.push(item.id);
            map.set(item.propertyId, existing);
        });

        return map;
    }, [unitInstallments, properties]);

    // Transform overdue installments into payment cards
    const overduePaymentCards = useMemo(() => {
        const overdueItems = getAllOverdueInstallments(unitInstallments, properties);

        // Group by property and calculate totals
        const propertyMap = new Map<string, { propertyName: string; amount: number; count: number }>();

        overdueItems.forEach((item) => {
            const existing = propertyMap.get(item.propertyId) || {
                propertyName: item.propertyName,
                amount: 0,
                count: 0,
            };
            // For overdue items, use due_amount directly
            existing.amount += item.due_amount || 0;
            existing.count += 1;
            propertyMap.set(item.propertyId, existing);
        });

        return Array.from(propertyMap.entries()).map(([propertyId, data]) => ({
            id: propertyId,
            propertyName: data.propertyName,
            amount: data.amount,
            paymentCount: data.count,
        }));
    }, [unitInstallments, properties]);

    // Pre-compute payment IDs map for fast lookup (upcoming due)
    const upcomingDuePaymentIdsMap = useMemo(() => {
        const upcomingItems = getAllUpcomingDueInstallments(unitInstallments, properties);
        const map = new Map<string, string[]>();

        upcomingItems.forEach((item) => {
            const existing = map.get(item.propertyId) || [];
            existing.push(item.id);
            map.set(item.propertyId, existing);
        });

        return map;
    }, [unitInstallments, properties]);

    // Transform upcoming due installments into payment cards
    const upcomingDuePaymentCards = useMemo(() => {
        const upcomingItems = getAllUpcomingDueInstallments(unitInstallments, properties);

        // Group by property and calculate totals
        const propertyMap = new Map<string, { propertyName: string; amount: number; count: number }>();

        upcomingItems.forEach((item) => {
            const existing = propertyMap.get(item.propertyId) || {
                propertyName: item.propertyName,
                amount: 0,
                count: 0,
            };
            // For upcoming items, use due_amount directly
            existing.amount += item.due_amount || 0;
            existing.count += 1;
            propertyMap.set(item.propertyId, existing);
        });

        return Array.from(propertyMap.entries()).map(([propertyId, data]) => ({
            id: propertyId,
            propertyName: data.propertyName,
            amount: data.amount,
            paymentCount: data.count,
        }));
    }, [unitInstallments, properties]);

    // Translate payment type for display (same logic as payment details cards — converts to Arabic in RTL)
    const translatePaymentType = useCallback(
        (type: string): string => {
            if (type === null || type === undefined) return '';
            if (typeof type !== 'string') return type as string;
            const typeLower = type.toLowerCase();
            if (typeLower.includes('handover')) return t('propertyDetails.handover');
            if (typeLower.includes('installment')) return t('propertyDetails.installment');
            if (typeLower.includes('down payment')) return t('propertyDetails.downPayment');
            if (typeLower.includes('adm fee') || typeLower.includes('adm')) return t('propertyDetails.admFee');
            if (typeLower.includes('technology fee') || typeLower.includes('convenience fee')) return t('payments.convenienceFee');
            return type;
        },
        [t],
    );

    // Transform paid installments into payment updates (most recent first)
    const paymentUpdates = useMemo(() => {
        const paidItems = getAllPaidInstallments(unitInstallments, properties, 10);

        return paidItems.map((item) => {
            const status = mapInstallmentStatusToPaymentStatus(item.category, item.status);
            // If paid_amount < due_amount, it's in-process
            const finalStatus = item.paid_amount && item.paid_amount < item.due_amount
                ? 'in-process'
                : status === 'paid' ? 'confirmed' : 'in-process';

            // Format payment date from due_date
            const paymentDate = item.due_date
                ? new Date(item.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
                : '';

            return {
                id: item.id,
                propertyName: item.propertyName,
                paymentTitle: translatePaymentType(item.type || item.payment_name),
                amount: item.paid_amount || item.amount || 0,
                status: finalStatus as 'in-process' | 'confirmed',
                paymentDate,
            };
        });
    }, [unitInstallments, properties, translatePaymentType]);

    // Calculate total properties count
    const totalProperties = useMemo(() => properties.length, [properties]);

    // Calculate total purchase price from all properties
    const totalPurchasePrice = useMemo(() => {
        return properties.reduce((sum, property) => sum + (property.totalPrice || 0), 0);
    }, [properties]);

    // Calculate upcoming handover date (earliest future handover date)
    const upcomingHandover = useMemo(() => {
        const now = new Date();
        const futureHandovers = properties
            .filter((p) => p.handoverDate && !p.handoverComplete)
            .map((p) => new Date(p.handoverDate!))
            .filter((date) => date > now)
            .sort((a, b) => a.getTime() - b.getTime());

        if (futureHandovers.length > 0) {
            const date = futureHandovers[0]!;
            const month = date.toLocaleDateString('en-US', { month: 'short' });
            const year = date.getFullYear();
            return `${month} ${year}`;
        }
        return null;
    }, [properties]);

    const renderPaymentCard = (data: PaymentCardData, category: 'overdue' | 'upcomingDue') => {
        // Format large numbers with ellipsis if needed
        const formatAmount = (amount: number | null | undefined): string => {
            const formatted = (amount ?? 0).toLocaleString();
            // If the formatted string is too long, truncate it
            if (formatted.length > 8) {
                return formatted.substring(0, 8) + '...';
            }
            return formatted;
        };

        return (
            <TouchableOpacity
                key={data.id}
                style={[styles.paymentCard, { backgroundColor: colors.backgroundLevel1 }]}
                onPress={() => handlePaymentCardPress(data.id, category)}
                activeOpacity={0.7}
            >
                <View style={styles.paymentCardContent}>
                    <Typography variant="caption" color="primary" style={styles.propertyName}>
                        {data.propertyName}
                    </Typography>
                    {/* Amount row with dirham icon, amount, and arrow aligned horizontally */}
                    {/* For Arabic: [Arrow] [DirhamIcon] 5,00,000 */}
                    {/* For English: [DirhamIcon] 5,00,000 [Arrow] */}
                    <View style={[styles.amountRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                        {isRTL ? (
                            <>
                                <View style={{ transform: [{ scaleX: -1 }] }}>
                                    <ChevronRightIcon size={18} color={colors.fontPrimary} />
                                </View>
                                <View style={[styles.amountContainer, { direction: 'ltr' }]}>
                                    <DirhamIcon size={15} color={colors.fontPrimary} />
                                    <Typography 
                                        variant="headline" 
                                        color="primary" 
                                        style={styles.amount} 
                                        numberOfLines={1}
                                        ellipsizeMode="tail"
                                    >
                                        {formatAmount(data.amount)}
                                    </Typography>
                                </View>
                            </>
                        ) : (
                            <>
                                <View style={[styles.amountContainer, { direction: 'ltr' }]}>
                                    <DirhamIcon size={15} color={colors.fontPrimary} />
                                    <Typography 
                                        variant="headline" 
                                        color="primary" 
                                        style={styles.amount} 
                                        numberOfLines={1}
                                        ellipsizeMode="tail"
                                    >
                                        {formatAmount(data.amount)}
                                    </Typography>
                                </View>
                                <View style={{ transform: [{ scaleX: 1 }] }}>
                                    <ChevronRightIcon size={18} color={colors.fontPrimary} />
                                </View>
                            </>
                        )}
                    </View>
                    <Typography variant="caption" color="primary" style={styles.paymentCount}>
                        {data.paymentCount} payments
                    </Typography>
                </View>
            </TouchableOpacity>
        );
    };

    const handlePaymentUpdatePress = (paymentId: string) => {
        if (Platform.OS === 'web') {
            // On web, open dialog instead of changing route
            setSelectedPaymentId(paymentId);
            setPaymentDetailsVisible(true);
            return;
        }
        router.push(`/(auth)/payment-details?id=${paymentId}` as any);
    };

    const renderPaymentUpdate = (data: PaymentUpdateData) => (
        <TouchableOpacity
            key={data.id}
            onPress={() => handlePaymentUpdatePress(data.id)}
            activeOpacity={0.7}
        >
            <Card variant="default" style={[styles.paymentUpdateCard, { shadowColor: 'transparent', backgroundColor: colors.backgroundLevel1 }]}>
                <Typography variant="footnote" color="primary" style={styles.updatePropertyName}>
                    {data.propertyName}
                </Typography>
                <View style={styles.updateContent}>
                    <Typography variant="subhead" color="primary" style={styles.updateTitle} numberOfLines={1}>
                        {data.paymentTitle}
                    </Typography>
                    <View style={[styles.updateAmountContainer, { direction: 'ltr' }]}>
                        <DirhamIcon size={13} color={colors.fontPrimary} />
                        <Typography variant="subhead" color="primary" style={styles.updateAmount} numberOfLines={1}>
                            {(data.amount ?? 0).toLocaleString()}
                        </Typography>
                    </View>
                </View>
                <View style={styles.statusContainer}>
                    {data.status === 'in-process' ? (
                        <>
                            <Typography variant="footnote" color="tertiary">
                                {t('home.inProcess')}
                            </Typography>
                            <CircleIcon size={12} color={colors.fontTertiary} />
                        </>
                    ) : (
                        <>
                            <CircleCheckIcon size={12} color={colors.fontSuccessText} strokeWidth={4} />
                            <Typography variant="footnote" color="success">
                                {t('home.paymentConfirmed')}
                            </Typography>
                        </>
                    )}
                </View>
            </Card>
        </TouchableOpacity>
    );

    const buildMissedPaymentsSection = () => {
        if (unitInstallmentsLoading && overduePaymentCards.length === 0) {
            return (
                <View key="missed-skeleton" style={styles.section}>
                    <View style={[styles.sectionHeader, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                        <CircleAlertIcon size={18} color={UI_PRIMITIVES.redLight} />
                        <Typography variant="headline" color="tertiary" style={[styles.sectionTitle, { paddingTop: isRTL ? 4 : 2 }]}>
                            {t('home.missedPayments')}
                        </Typography>
                    </View>
                    <View style={styles.horizontalScrollContainer}>
                        <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            contentContainerStyle={styles.horizontalScrollContent}
                        >
                            {[1, 2, 3].map((i) => (
                                <View key={i} style={{ marginRight: 12, width: 160 }}>
                                    <PaymentCardSkeleton />
                                </View>
                            ))}
                        </ScrollView>
                    </View>
                </View>
            );
        }

        if (overduePaymentCards.length > 0) {
            return (
                <View key="missed" style={styles.section}>
                    <TouchableOpacity
                        style={[styles.sectionHeader, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}
                        onPress={() => {
                            trackDynatraceEvent({
                                p_screen: 'home',
                                p_actionName: 'View All Missed Payments',
                                eventType: 'action',
                            });
                            trackPayAllMissed();
                            const overdueItems = getAllOverdueInstallments(unitInstallments, properties);
                            const paymentIds = selectOnlinePaymentIds(overdueItems);

                            router.push({
                                pathname: '/(auth)/property-payments',
                                params: paymentIds.length > 0
                                    ? {
                                        tab: 'upcoming',
                                        selectedPaymentIds: paymentIds.join(','),
                                    }
                                    : { tab: 'upcoming' },
                            } as any);
                        }}
                        activeOpacity={0.7}
                    >
                        <CircleAlertIcon size={18} color={UI_PRIMITIVES.redLight} />
                        <Typography variant="headline" color="tertiary" style={[styles.sectionTitle, { paddingTop: isRTL ? 4 : 2 }]}>
                            {t('home.missedPayments')}
                        </Typography>
                    </TouchableOpacity>
                    <View style={styles.horizontalScrollContainer}>
                        <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            contentContainerStyle={styles.horizontalScrollContent}
                        >
                            {overduePaymentCards.map((card) => renderPaymentCard(card, 'overdue'))}
                        </ScrollView>
                    </View>
                </View>
            );
        }

        return null;
    };

    const buildDueSoonPaymentsSection = () => {
        if (unitInstallmentsLoading && upcomingDuePaymentCards.length === 0) {
            return (
                <View key="due-soon-skeleton" style={styles.section}>
                    <View style={[styles.sectionHeader, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                        <ClockIcon size={18} color={UI_PRIMITIVES.yellowDark} />
                        <Typography variant="headline" color="tertiary" style={[styles.sectionTitle, { paddingTop: isRTL ? 4 : 2 }]}>
                            {t('home.paymentsDueIn30Days')}
                        </Typography>
                    </View>
                    <View style={styles.horizontalScrollContainer}>
                        <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            contentContainerStyle={styles.horizontalScrollContent}
                        >
                            {[1, 2, 3].map((i) => (
                                <View key={i} style={{ marginRight: 12, width: 160 }}>
                                    <PaymentCardSkeleton />
                                </View>
                            ))}
                        </ScrollView>
                    </View>
                </View>
            );
        }

        if (upcomingDuePaymentCards.length > 0) {
            return (
                <View key="due-soon" style={styles.section}>
                    <TouchableOpacity
                        style={[styles.sectionHeader, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}
                        onPress={() => {
                            trackDynatraceEvent({
                                p_screen: 'home',
                                p_actionName: 'View All Due Soon Payments',
                                eventType: 'action',
                            });
                            const upcomingItems = getAllUpcomingDueInstallments(unitInstallments, properties);
                            const paymentIds = selectOnlinePaymentIds(upcomingItems);

                            router.push({
                                pathname: '/(auth)/property-payments',
                                params: paymentIds.length > 0
                                    ? {
                                        tab: 'upcoming',
                                        selectedPaymentIds: paymentIds.join(','),
                                    }
                                    : { tab: 'upcoming' },
                            } as any);
                        }}
                        activeOpacity={0.7}
                    >
                        <ClockIcon size={18} color={UI_PRIMITIVES.yellowDark} />
                        <Typography variant="headline" color="tertiary" style={[styles.sectionTitle, { paddingTop: isRTL ? 4 : 2 }]}>
                            {t('home.paymentsDueIn30Days')}
                        </Typography>
                    </TouchableOpacity>
                    <View style={styles.horizontalScrollContainer}>
                        <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            contentContainerStyle={styles.horizontalScrollContent}
                        >
                            {upcomingDuePaymentCards.map((card) => renderPaymentCard(card, 'upcomingDue'))}
                        </ScrollView>
                    </View>
                </View>
            );
        }

        return null;
    };

    const buildPaymentUpdatesSection = () => {
        if (unitInstallmentsLoading && paymentUpdates.length === 0) {
            return (
                <View key="updates-skeleton" style={styles.section}>
                    <View style={[styles.sectionHeader, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                        <ReceiptTextIcon size={18} color={colors.fontPrimary} />
                        <Typography variant="headline" color="tertiary" style={[styles.sectionTitle, { paddingTop: isRTL ? 4 : 2 }]}>
                            {t('home.paymentUpdates')}
                        </Typography>
                    </View>
                    <View style={styles.horizontalScrollContainer}>
                        <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            contentContainerStyle={styles.horizontalScrollContent}
                        >
                            {[1, 2, 3].map((i) => (
                                <View key={i} style={{ marginRight: 12, width: 280 }}>
                                    <Card variant="default" style={[styles.paymentUpdateCard, { shadowColor: 'transparent', backgroundColor: colors.backgroundLevel1 }]}>
                                        <Skeleton width={120} height={12} />
                                        <View style={styles.updateContent}>
                                            <Skeleton width={180} height={14} style={{ marginTop: 8 }} />
                                            <Skeleton width={80} height={14} style={{ marginTop: 8 }} />
                                        </View>
                                        <View style={styles.statusContainer}>
                                            <Skeleton width={100} height={12} style={{ marginTop: 8 }} />
                                        </View>
                                    </Card>
                                </View>
                            ))}
                        </ScrollView>
                    </View>
                </View>
            );
        }

        if (paymentUpdates.length > 0) {
            return (
                <View key="updates" style={styles.section}>
                    <View style={[styles.sectionHeader, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                        <ReceiptTextIcon size={18} color={colors.fontPrimary} />
                        <Typography variant="headline" color="tertiary" style={[styles.sectionTitle, { paddingTop: isRTL ? 4 : 2 }]}>
                            {t('home.paymentUpdates')}
                        </Typography>
                    </View>
                    <View style={styles.horizontalScrollContainer}>
                        <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            contentContainerStyle={styles.horizontalScrollContent}
                        >
                            {paymentUpdates.map(renderPaymentUpdate)}
                        </ScrollView>
                    </View>
                </View>
            );
        }

        return null;
    };

    const renderPaymentSections = () => {
        const sections = isRTL
            ? [buildPaymentUpdatesSection(), buildDueSoonPaymentsSection(), buildMissedPaymentsSection()]
            : [buildMissedPaymentsSection(), buildDueSoonPaymentsSection(), buildPaymentUpdatesSection()];
        return sections.filter(Boolean);
    };

    const [ventureGridWidth, setVentureGridWidth] = useState<number | null>(null);

    // Dynamic venture grid sizing - evenly distribute edge to edge
    const venturesGap = 12;
    const horizontalPadding = 32; // section padding (16px each side)
    const minCardWidth = 120; // Minimum card width before wrapping
    const availableVenturesWidth = ventureGridWidth ?? (screenWidth - horizontalPadding);
    
    // Calculate columns based on available space (always at least 2 columns on mobile)
    const ventureColumns = Math.max(2, Math.floor((availableVenturesWidth + venturesGap) / (minCardWidth + venturesGap)));
    
    // Calculate card width to evenly fill available space edge to edge
    const totalGapWidth = venturesGap * (ventureColumns - 1);
    const ventureCardWidth = Math.floor((availableVenturesWidth - totalGapWidth) / ventureColumns);
    
    // Maintain aspect ratio: Figma ratio ~206/280 for web, ~86/112 for mobile
    const ventureCardHeight = Math.round(ventureCardWidth * (86 / 112));
    
    const ventureImageWidth = ventureCardWidth;
    const ventureImageHeight = ventureCardHeight;

    const renderVentureCard = (data: any, index: number) => {
        return (
            <TouchableOpacity
                key={data.id}
                style={[
                    styles.ventureCard,
                    {
                        backgroundColor: colors.backgroundLevel1,
                        borderColor: colors.outlineInactive,
                        width: ventureCardWidth,
                        height: ventureCardHeight,
                    },
                ]}
                onPress={() => handleVenturePress(data.id)}
                activeOpacity={0.85}
            >
                <Image
                    source={{ uri: data.logoUrl }}
                    style={[
                        styles.ventureImage,
                        {
                            width: ventureImageWidth,
                            height: ventureImageHeight,
                        },
                    ]}
                    contentFit="contain"
                    contentPosition="center"
                    cachePolicy="memory-disk"
                    transition={200}
                />
            </TouchableOpacity>
        );
    };

    // Show skeleton loading for initial profile load
    // After profile loads, show skeleton states for units progressively
    if (profileLoading) {
        return (
            <View style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}>
                <ScrollView
                    style={styles.scrollView}
                    contentContainerStyle={styles.scrollContent}
                    showsVerticalScrollIndicator={false}
                    contentInsetAdjustmentBehavior="never"
                >
                    {/* Header Background Skeleton */}
                    <View style={[styles.headerBackground, { height: 240 }]}>
                        <Skeleton width="100%" height={240} />
                        {/* Profile Notification Header Skeleton */}
                        <View style={[styles.headerTopBar, { top: 0 }]}>
                            <View style={{ paddingTop: insets.top + 8, paddingHorizontal: 16, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Skeleton width={120} height={24} />
                                <View style={{ flexDirection: 'row', gap: 16 }}>
                                    <Skeleton width={24} height={24} variant="circular" />
                                    <Skeleton width={24} height={24} variant="circular" />
                                </View>
                            </View>
                        </View>
                        {/* Greeting Skeleton */}
                        <View style={styles.greetingContainer}>
                            <Skeleton width={200} height={32} />
                        </View>
                    </View>

                    {/* Stats Card Skeleton */}
                    <View style={styles.statsCardWrapper}>
                        <Card variant="default" style={[
                            styles.statsCard,
                            {
                                backgroundColor: colors.backgroundLevel1,
                            }
                        ]}>
                            <View style={styles.statSection}>
                                <Skeleton width={100} height={12} />
                                <Skeleton width={40} height={20} style={{ marginTop: 4 }} />
                            </View>
                            <View style={styles.statSection}>
                                <Skeleton width={100} height={12} />
                                <View style={styles.priceContainer}>
                                    <Skeleton width={12} height={12} variant="circular" />
                                    <Skeleton width={80} height={20} style={{ marginLeft: 4 }} />
                                </View>
                            </View>
                        </Card>
                    </View>

                    {/* Payment Sections Skeletons */}
                    <View style={styles.section}>
                        <View style={[styles.sectionHeader, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                            <Skeleton width={18} height={18} variant="circular" />
                            <Skeleton width={140} height={18} />
                        </View>
                        <View style={styles.horizontalScrollContainer}>
                            <ScrollView
                                horizontal
                                showsHorizontalScrollIndicator={false}
                                contentContainerStyle={styles.horizontalScrollContent}
                            >
                                {[1, 2, 3].map((i) => (
                                    <View key={i} style={{ marginRight: 12, width: 160 }}>
                                        <PaymentCardSkeleton />
                                    </View>
                                ))}
                            </ScrollView>
                        </View>
                    </View>

                    <View style={styles.section}>
                        <View style={[styles.sectionHeader, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                            <Skeleton width={18} height={18} variant="circular" />
                            <Skeleton width={180} height={18} />
                        </View>
                        <View style={styles.horizontalScrollContainer}>
                            <ScrollView
                                horizontal
                                showsHorizontalScrollIndicator={false}
                                contentContainerStyle={styles.horizontalScrollContent}
                            >
                                {[1, 2, 3].map((i) => (
                                    <View key={i} style={{ marginRight: 12, width: 160 }}>
                                        <PaymentCardSkeleton />
                                    </View>
                                ))}
                            </ScrollView>
                        </View>
                    </View>
                </ScrollView>
            </View>
        );
    }

    // Show error UI if profile fetch failed
    if (profileError) {
        return (
            <View style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}>
                <ProfileNotificationHeader
                    showLogo={true}
                    onProfilePress={() => router.push('/profile')}
                    onNotificationPress={() => router.push('/notifications')}
                />
                <View style={styles.errorContainer}>
                    <CircleAlertIcon size={48} color={colors.fontErrorText} />
                    <Typography variant="headline" color="errorText" style={styles.errorTitle}>
                        {t('common.error')}
                    </Typography>
                    <Typography variant="body" color="tertiary" style={styles.errorMessage}>
                        {profileError}
                    </Typography>
                    <Button
                        title={t('common.retry') || 'Retry'}
                        variant="primary"
                        onPress={retry}
                        style={styles.retryButton}
                    />
                </View>
            </View>
        );
    }

    // Conditionally render web vs mobile version
    if (Platform.OS === 'web') {
        const { HomeScreenWeb } = require('./HomeScreenWeb');
        // Use the user variable already defined at line 237 (before this conditional)
        const userEmail = user?.primary_email || user?.secondary_email || user?.tertiary_email || '';
        const ventures = (venturesQuery.data || []).map(v => ({ id: v.id, logoUrl: v.logoUrl || '' }));

        return (
            <View style={{ flex: 1 }}>
                <HomeScreenWeb
                userName={userName}
                userEmail={userEmail}
                totalProperties={totalProperties}
                totalPurchasePrice={totalPurchasePrice}
                overduePaymentCards={overduePaymentCards}
                upcomingDuePaymentCards={upcomingDuePaymentCards}
                paymentUpdates={paymentUpdates}
                homeCoverImage={homeCoverImage}
                ventures={ventures}
                profileLoading={profileLoading}
                unitInstallmentsLoading={unitInstallmentsLoading}
                onPaymentCardPress={handlePaymentCardPress}
                onPaymentUpdatePress={handlePaymentUpdatePress}
                onVenturePress={handleVenturePress}
                onViewAllMissed={() => {
                    trackDynatraceEvent({
                        p_screen: 'home',
                        p_actionName: 'View All Missed Payments',
                        eventType: 'action',
                    });
                    trackPayAllMissed();
                    const overdueItems = getAllOverdueInstallments(unitInstallments, properties);
                    const paymentIds = selectOnlinePaymentIds(overdueItems);
                    router.push({
                        pathname: '/(auth)/property-payments',
                        params: paymentIds.length > 0
                            ? {
                                tab: 'upcoming',
                                selectedPaymentIds: paymentIds.join(','),
                            }
                            : { tab: 'upcoming' },
                    } as any);
                }}
                onViewAllDueSoon={() => {
                    trackDynatraceEvent({
                        p_screen: 'home',
                        p_actionName: 'View All Due Soon Payments',
                        eventType: 'action',
                    });
                    const upcomingItems = getAllUpcomingDueInstallments(unitInstallments, properties);
                    const paymentIds = selectOnlinePaymentIds(upcomingItems);
                    router.push({
                        pathname: '/(auth)/property-payments',
                        params: paymentIds.length > 0
                            ? {
                                tab: 'upcoming',
                                selectedPaymentIds: paymentIds.join(','),
                            }
                            : { tab: 'upcoming' },
                    } as any);
                }}
                onViewAllHistory={() => {
                    trackDynatraceEvent({
                        p_screen: 'home',
                        p_actionName: 'View Payment History',
                        eventType: 'action',
                    });
                    router.push('/(auth)/property-payments?tab=history' as any);
                }}
                onViewAllProperties={navigateToPropertiesList}
            />
            {/* Payment details dialog for web – route is not changed */}
            {paymentDetailsVisible && (
                <PaymentDetailsDialog
                    visible={paymentDetailsVisible}
                    paymentId={selectedPaymentId}
                    onClose={() => setPaymentDetailsVisible(false)}
                />
            )}
            </View>
        );
    }

    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}>
            <ScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
                contentInsetAdjustmentBehavior="never"
            >
                {/* Header Background - 240px height, starts at y=0, extends behind status bar */}
                <View style={[styles.headerBackground, { height: 240 }]}>
                    {homeCoverImage && (
                        <Image
                            source={{ uri: homeCoverImage }}
                            style={styles.headerImage}
                            contentFit="cover"
                            cachePolicy="memory-disk"
                            transition={200}
                        />
                    )}
                    {/* Gradient Overlay - matches Figma (black to transparent with mix-blend-multiply effect) */}
                    <LinearGradient
                        colors={['rgba(0, 0, 0, 0)', 'rgba(53, 53, 53, 0.792)', 'rgba(0, 0, 0, 1)']}
                        locations={[0, 0.42119, 1]}
                        style={styles.headerOverlayGradient}
                    />

                    {/* Profile Notification Header - positioned at y=50 (below status bar) per Figma */}
                    <View style={[styles.headerTopBar, { top: 0 }]}>
                        <ProfileNotificationHeader
                            showLogo={true}
                            onProfilePress={() => router.push('/profile')}
                            onNotificationPress={() => router.push('/notifications')}
                            style={{ paddingTop: insets.top + 8 }}
                        />
                    </View>

                    {/* Greeting Text - positioned higher to avoid overlap with stats card */}
                    <View style={styles.greetingContainer}>
                        {/* exception for font size as suggested by marketing team */}
                        <Typography variant="title1" color="white" style={styles.greeting} numberOfLines={2}>
                            {t(getGreetingTranslationKey() as any)} {userName}{!isRTL ? '!' : ''}
                        </Typography>
                    </View>
                </View>

                {/* Stats Card - 50% overlapping the header image */}
                <View style={styles.statsCardWrapper}>
                    <Card variant="default" style={[
                        styles.statsCard,
                        {
                            backgroundColor: colors.backgroundLevel1,
                            shadowColor: colors.fontPrimary,
                        }
                    ]}>
                        <TouchableOpacity
                            style={styles.statSection}
                            onPress={navigateToPropertiesList}
                            activeOpacity={0.7}
                            accessibilityRole="button"
                            accessibilityLabel={t('home.totalProperties')}
                        >
                            <Typography variant="footnote" color="tertiary" style={styles.statLabel}>
                                {t('home.totalProperties')}
                            </Typography>
                            <Typography variant="headline" color="primary" style={styles.statValue}>
                                {totalProperties}
                            </Typography>
                        </TouchableOpacity>
                        <View style={styles.statSection}>
                            <Typography variant="footnote" color="tertiary" style={styles.statLabel}>
                                {t('home.purchasePrice')}
                            </Typography>
                            <View style={[styles.priceContainer, { direction: 'ltr' }]}>
                                <DirhamIcon size={15} color={colors.fontPrimary} />
                                <Typography variant="headline" color="primary" style={styles.priceValue}>
                                    {totalPurchasePrice.toLocaleString()}
                                </Typography>
                            </View>
                        </View>
                    </Card>
                </View>

                {renderPaymentSections()}

                {/* Bloom Ventures Section */}
                <View style={styles.section}>
                    <Typography variant="headline" color="primary" style={styles.venturesTitle}>
                        {t('home.bloomVentures')}
                    </Typography>
                <View
                    style={styles.venturesGrid}
                    onLayout={(event) => setVentureGridWidth(event.nativeEvent.layout.width)}
                >
                        {venturesQuery.isLoading
                            ? [...Array(6)].map((_, idx) => (
                                <Skeleton
                                    key={idx}
                                    height={ventureCardHeight}
                                    borderRadius={12}
                                    style={{ width: ventureCardWidth, marginBottom: 16 }}
                                />
                            ))
                            : (venturesQuery.data || []).map((venture, index) => renderVentureCard(venture, index))}
                    </View>
                </View>
            </ScrollView>

            {/* Payment details dialog for web – route is not changed */}
            {paymentDetailsVisible && (
                <PaymentDetailsDialog
                    visible={paymentDetailsVisible}
                    paymentId={selectedPaymentId}
                    onClose={() => setPaymentDetailsVisible(false)}
                />
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        marginTop: 0,
        paddingTop: 0,
        // backgroundColor applied in component for theme support
    },
    scrollView: {
        flex: 1,
        marginTop: 0,
        paddingTop: 0,
    },
    scrollContent: {
        paddingBottom: 100,
        paddingTop: 0,
    },
    headerBackground: {
        width: '100%',
        height: 240, // Figma: 240px height
        position: 'relative',
        overflow: 'hidden', // Ensure content doesn't overflow
    },
    headerImage: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
    },
    headerOverlayGradient: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        opacity: 0.5, // Figma: opacity 50%
    },
    headerTopBar: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 30,
    },
    greetingContainer: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 120, // Position higher up in the header to avoid overlap with stats card
        width: '100%',
        paddingHorizontal: 16,
        zIndex: 20,
        alignItems: 'center',
        justifyContent: 'center',
    },
    greeting: {
        textAlign: 'center',
        fontSize: 24,
    },
    // Stats Card - 50% overlapping the header image (positioned at 200px from top, header ends at 240px)
    // Assuming card height ~80px, 50% = 40px overlap, so card starts at 240 - 40 = 200px
    statsCardWrapper: {
        marginHorizontal: 16,
        marginTop: -40, // Negative margin to overlap 50% of card height on the image
        marginBottom: 20, // Figma: gap: 20px
        zIndex: 10,
    },
    statsCard: {
        flexDirection: 'row',
        paddingVertical: 16,
        paddingHorizontal: 4,
        gap: 12,
        // Enhanced shadow for better depth
        // shadowColor applied in component for theme support
        shadowOffset: {
            width: 0,
            height: 4,
        },
        shadowOpacity: 0.15,
        shadowRadius: 8,
        elevation: 8,
    },
    statSection: {
        flex: 1,
        alignItems: 'center',
        paddingVertical: 16,
        paddingHorizontal: 12,
    },
    statLabel: {
        marginBottom: 4,
        textAlign: 'center',
    },
    statValue: {
        textAlign: 'center',
    },
    priceContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    priceValue: {
        textAlign: 'center',
    },
    section: {
        marginBottom: 20,
        paddingHorizontal: 16,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        height: 24,
        marginBottom: 8,
    },
    sectionTitle: {
        flex: 1,
    },
    horizontalScrollContent: {
        gap: 12,
        paddingLeft: 16, // Left padding to prevent cards from touching edge
        paddingRight: 16, // Right padding to show there are more items
    },
    horizontalScrollContainer: {
        marginHorizontal: -16, // Negative margin to extend to screen edges
    },
    paymentCard: {
        // backgroundColor applied in component for theme support
        borderRadius: 12,
        width: 141,
        height: 84,
        position: 'relative', // For absolute positioning
    },
    paymentCardContent: {
        position: 'absolute',
        top: 12, // Exact Figma positioning
        left: 12, // Exact Figma positioning
        right: 12, // Add right padding for arrow
        height: 60, // Exact Figma measurement
        gap: 8,
    },
    propertyName: {
        width: '100%',
    },
    amountRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
    },
    amountContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        flexShrink: 1, // Allow container to shrink if needed
        minWidth: 0, // Allow flex children to shrink below their content size
    },
    amount: {
        flexShrink: 1, // Allow text to shrink for large numbers
        minWidth: 0, // Allow text to shrink below content size
        paddingTop: 2,
    },
    paymentCount: {
        width: '100%',
    },
    paymentUpdateCard: {
        width: 256,
        height: 77, // Exact Figma measurement
        padding: 0,
        position: 'relative', // For absolute positioning
        overflow: 'hidden',
    },
    updatePropertyName: {
        position: 'absolute',
        top: 12, // Exact Figma positioning
        left: 12, // Exact Figma positioning
        right: 12,
    },
    updateContent: {
        position: 'absolute',
        top: 30, // Exact Figma positioning
        left: 12, // Exact Figma positioning
        right: 12,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    updateTitle: {
        flex: 1,
        marginRight: 8,
    },
    updateAmountContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
        flexShrink: 0,
    },
    updateAmount: {
        flexShrink: 0,
        paddingTop: 2,
    },
    statusContainer: {
        position: 'absolute',
        bottom: 12, // Position from bottom instead of top
        left: 12,
        right: 12,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 4,
    },
    emptyState: {
        paddingVertical: 16,
        paddingHorizontal: 12,
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 200,
    },
    venturesTitle: {
        marginBottom: 12,
    },
    ventureCard: {
        // backgroundColor applied in component for theme support
        borderRadius: 12,
        borderWidth: 1,
        overflow: 'hidden', // ensure image covers card without spilling
    },
    ventureImage: {
        // image fills the entire card; border radius comes from card
    },
    venturesGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        columnGap: 12,
        rowGap: 16,
        justifyContent: 'space-between',
        alignContent: 'flex-start',
    },
    // Error state styles
    errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 24,
        gap: 16,
    },
    errorTitle: {
        textAlign: 'center',
        marginTop: 8,
    },
    errorMessage: {
        textAlign: 'center',
        marginBottom: 8,
    },
    retryButton: {
        marginTop: 8,
        minWidth: 120,
        alignSelf: 'center',
    },
});
